// Elastic Audio: Zuordnung Timeline ↔ Quelle für Clips mit Warp-Punkten (reine Funktionen, ohne Audio).
// Ein Warp-Punkt { s, b }: Quellstelle s (Sekunden in der Datei) erklingt b Viertel nach dem Clip-Anfang.
// Zwischen den Punkten linear (in Vierteln), außen mit der Steigung des Randstücks. Über die Tempo-Spur
// wird daraus eine Zeitkarte; sie ist stückweise linear mit Knicken an Warp-Punkten und Tempowechseln.
import { tuneActive } from './pitch.ts';
import type { Clip, Project, Track } from '@srs/shared';
import { beatAt, secAt, tempoEvents } from '@srs/shared';

type Pt = { s: number; b: number };
const sorted = (pts: Pt[]) => [...pts].sort((a, b) => a.b - b.b);

/** Quellstelle (s) zu Vierteln ab Clip-Anfang */
export function srcOfBeat(pts: Pt[], b: number): number {
  const p = sorted(pts);
  if (p.length === 1) return p[0].s + (b - p[0].b) * 0.5;
  let i = 0;
  while (i < p.length - 2 && b > p[i + 1].b) i++;
  const a = p[i], c = p[i + 1], k = (c.s - a.s) / Math.max(1e-9, c.b - a.b);
  return a.s + (b - a.b) * k;
}
/** Viertel ab Clip-Anfang zu einer Quellstelle (Umkehrung, Punkte sind monoton) */
export function beatOfSrc(pts: Pt[], s: number): number {
  const p = sorted(pts);
  if (p.length === 1) return p[0].b + (s - p[0].s) / 0.5;
  let i = 0;
  while (i < p.length - 2 && s > p[i + 1].s) i++;
  const a = p[i], c = p[i + 1], k = (c.b - a.b) / Math.max(1e-9, c.s - a.s);
  return a.b + (s - a.s) * k;
}

/** Zuordnung eines Elastic-Clips: Timeline-Sekunde ↔ Quell-Sekunde */
export function clipMap(p: Project, c: Clip) {
  const pts = c.warp?.points ?? [];
  const b0 = beatAt(p, c.start);
  if (!pts.length) return { toSrc: (t: number) => c.offset + (t - c.start), toTime: (s: number) => c.start + (s - c.offset) };
  return {
    toSrc: (t: number) => srcOfBeat(pts, beatAt(p, t) - b0),
    toTime: (s: number) => secAt(p, b0 + beatOfSrc(pts, s)),
  };
}

/** Warp anlegen, wie der Clip gerade klingt (1:1 in der Zeit), damit er ab jetzt tick-basiert ist */
export function ensureWarp(p: Project, c: Clip) {
  if (c.kind !== 'audio' || c.warp) return;
  const b0 = beatAt(p, c.start);
  c.warp = { points: [{ s: c.offset, b: 0 }, { s: c.offset + c.duration, b: beatAt(p, c.start + c.duration) - b0 }] };
}
/** Braucht der Clip eine gerechnete Fassung? (Nein, wenn er zeitlich 1:1 und ohne Tonhöhe spielt) */
export function needsRender(p: Project, c: Clip): boolean {
  if (!c.warp) return false;
  if (tuneActive(c.tune)) return true;
  if (Math.abs(c.warp.pitch ?? 0) > 1e-6) return true;
  const m = clipMap(p, c);
  for (const t of [c.start, c.start + c.duration * 0.25, c.start + c.duration * 0.5, c.start + c.duration * 0.75, c.start + c.duration, ...breakpoints(p, c, c.start, c.start + c.duration)]) {
    if (Math.abs(m.toSrc(t) - (c.offset + (t - c.start))) > 0.0005) return true;
  }
  return false;
}
/** Knickstellen der Zeitkarte in [a, b] (Timeline-Sekunden): Warp-Punkte und Tempowechsel */
export function breakpoints(p: Project, c: Clip, a: number, b: number): number[] {
  const b0 = beatAt(p, c.start), out = [a, b];
  for (const w of c.warp?.points ?? []) { const t = secAt(p, b0 + w.b); if (t > a && t < b) out.push(t); }
  for (const e of tempoEvents(p)) if (e.sec > a && e.sec < b) out.push(e.sec);
  return [...new Set(out.map((x) => Math.round(x * 1e7) / 1e7))].sort((x, y) => x - y);
}

// ---- Edit-Operationen auf Elastic-Clips (Offset bleibt die Quellstelle am Clip-Anfang) ----
/** Clip-Anfang auf newStart setzen, Inhalt bleibt zeitlich, wo er ist (Trim, Split) */
export function rebaseStart(p: Project, c: Clip, newStart: number) {
  if (!c.warp) return;
  const m = clipMap(p, c), db = beatAt(p, newStart) - beatAt(p, c.start);
  c.offset = m.toSrc(newStart);
  c.warp = { ...c.warp, points: c.warp.points.map((w) => ({ s: w.s, b: w.b - db })) };
}
/** Grenzen für Trim: so weit die Quelle reicht */
export function warpLimits(p: Project, c: Clip, srcLen?: number): { minStart: number; maxEnd: number } {
  const m = clipMap(p, c);
  return { minStart: Math.max(0, m.toTime(0)), maxEnd: srcLen === undefined ? c.start + c.duration : m.toTime(srcLen) };
}
/** Spur ist Elastic? */
export const isElastic = (t: Track | undefined) => !!t?.elastic && t.kind === 'audio';
